/**
 * Finalize dialog: typed printed name + date (D15). `window.confirm` cannot take
 * typed input, so this is a real modal.
 */
import React, { useEffect, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { reviewDesk } from '@/design-system';
import { useTranslation } from '@/hooks/useTranslation';
import type { FormCompletionData, IcfRuleCheck } from '@/types/mentorEngagements';
import { ActionButton, TextField, mentorStyles } from './shared';
import { ruleLabelKey } from './IcfRulesPanel';
import { WorkspaceModal } from './WorkspaceModal';
import { checkboxA11y } from '@/utils/checkboxA11y';

interface FinalizeModalProps {
  visible: boolean;
  title?: string;
  defaultPrintedName?: string | null;
  /** Unmet §5 checks are listed in the confirm step but never block. */
  warnings?: IcfRuleCheck[];
  onClose: () => void;
  onConfirm: (printedName: string, signedDate: string, completion: FormCompletionData) => Promise<void>;
}

const COMPLETION_CHECKS = [
  ['mcs_active', 'mcsActive'],
  ['no_conflict', 'noConflict'],
  ['requirements_understood', 'requirementsUnderstood'],
  ['feedback_reviewed', 'feedbackReviewed'],
  ['sharing_permission', 'sharingPermission'],
] as const;

function emptyCompletion(): FormCompletionData {
  return {
    mcs_active: false,
    no_conflict: false,
    requirements_understood: false,
    feedback_reviewed: false,
    sharing_permission: false,
    pre_reflection: '',
    post_reflection: '',
  };
}

export function FinalizeModal({
  visible,
  title,
  defaultPrintedName,
  warnings = [],
  onClose,
  onConfirm,
}: FinalizeModalProps) {
  const { t } = useTranslation();
  const [printedName, setPrintedName] = useState('');
  const [signedDate, setSignedDate] = useState(new Date().toISOString().slice(0, 10));
  const [error, setError] = useState<string | null>(null);
  const [attested, setAttested] = useState(false);
  const [completion, setCompletion] = useState<FormCompletionData>(emptyCompletion);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setAttested(false);
    setCompletion(emptyCompletion());
    setPrintedName(defaultPrintedName || '');
    setSignedDate(new Date().toISOString().slice(0, 10));
    setError(null);
  }, [visible, defaultPrintedName]);

  const completionReady = COMPLETION_CHECKS.every(([key]) => completion[key])
    && Boolean(completion.pre_reflection.trim()) && Boolean(completion.post_reflection.trim());

  const confirm = async () => {
    if (saving || !attested) return;
    if (!completionReady) {
      setError(t('mentorEngagements.finalize.completionRequired'));
      return;
    }
    if (!printedName.trim()) {
      setError(t('mentorEngagements.finalize.nameRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onConfirm(printedName.trim(), signedDate, {
        ...completion,
        pre_reflection: completion.pre_reflection.trim(),
        post_reflection: completion.post_reflection.trim(),
      });
      onClose();
    } catch {
      setError(t('mentorEngagements.finalize.error'));
    } finally {
      setSaving(false);
    }
  };

  const unmet = warnings.filter((check) => !check.ok);

  return (
    <WorkspaceModal
      testID="finalize_modal"
      visible={visible}
      busy={saving}
      title={title || t('mentorEngagements.finalize.title')}
      subtitle={t('mentorEngagements.finalize.body')}
      onClose={onClose}
      footer={(
        <>
          <ActionButton label={t('mentorEngagements.finalize.cancel')} onPress={onClose} disabled={saving} />
          <ActionButton
            testID="finalize_confirm"
            label={t('mentorEngagements.finalize.confirm')}
            onPress={confirm}
            variant="primary"
            disabled={saving || !attested || !completionReady}
          />
        </>
      )}
    >
      {unmet.length > 0 ? (
        <View style={{
          gap: 6,
          padding: 12,
          borderRadius: 12,
          backgroundColor: reviewDesk.amberSoft,
        }}>
          <View style={mentorStyles.row}>
            <MaterialIcons name="error-outline" size={17} color={reviewDesk.amber} />
            <Text style={[mentorStyles.bodyText, { fontWeight: '700' }]}>
              {t('mentorEngagements.finalize.warningsTitle')}
            </Text>
          </View>
          {unmet.map((check) => (
            <Text key={check.key} style={mentorStyles.mutedText}>• {t(ruleLabelKey(check))}</Text>
          ))}
        </View>
      ) : null}

      <TextField
        editable={!saving}
        testID="finalize_printed_name"
        label={t('mentorEngagements.finalize.printedName')}
        value={printedName}
        onChangeText={setPrintedName}
        placeholder={t('mentorEngagements.finalize.printedNamePlaceholder')}
      />
      <TextField
        editable={!saving}
        label={t('mentorEngagements.finalize.date')}
        value={signedDate}
        onChangeText={setSignedDate}
        placeholder="YYYY-MM-DD"
      />
      {COMPLETION_CHECKS.map(([key, labelKey]) => (
        <Pressable
          key={key}
          testID={`finalize_${key}`}
          {...checkboxA11y(completion[key], saving)}
          accessibilityLabel={t(`mentorEngagements.finalize.${labelKey}`)}
          disabled={saving}
          onPress={() => setCompletion((current) => ({ ...current, [key]: !current[key] }))}
          style={[mentorStyles.row, { flexWrap: 'nowrap', alignItems: 'flex-start' }]}
        >
          <MaterialIcons name={completion[key] ? 'check-box' : 'check-box-outline-blank'} size={22} color={reviewDesk.accent} />
          <Text style={[mentorStyles.bodyText, { flex: 1 }]}>{t(`mentorEngagements.finalize.${labelKey}`)}</Text>
        </Pressable>
      ))}
      <TextField
        testID="finalize_pre_reflection"
        label={t('mentorEngagements.finalize.preReflection')}
        value={completion.pre_reflection}
        onChangeText={(value) => setCompletion((current) => ({ ...current, pre_reflection: value }))}
        multiline
        editable={!saving}
      />
      <TextField
        testID="finalize_post_reflection"
        label={t('mentorEngagements.finalize.postReflection')}
        value={completion.post_reflection}
        onChangeText={(value) => setCompletion((current) => ({ ...current, post_reflection: value }))}
        multiline
        editable={!saving}
      />
      <Pressable
        testID="finalize_attestation"
        {...checkboxA11y(attested, saving)}
        disabled={saving}
        onPress={() => setAttested((current) => !current)}
        style={[mentorStyles.row, { flexWrap: 'nowrap', alignItems: 'flex-start' }]}
      >
        <MaterialIcons name={attested ? 'check-box' : 'check-box-outline-blank'} size={22} color={reviewDesk.accent} />
        <Text style={[mentorStyles.bodyText, { flex: 1 }]}>{t('mentorEngagements.finalize.attestation')}</Text>
      </Pressable>
      {error ? <Text style={mentorStyles.errorText}>{error}</Text> : null}
    </WorkspaceModal>
  );
}

export default FinalizeModal;
